import React, { createContext, useContext, useState, useEffect } from 'react';

const KYCContext = createContext();

export const useKYC = () => {
  const context = useContext(KYCContext);
  if (!context) {
    throw new Error('useKYC must be used within a KYCProvider');
  }
  return context;
};

export const KYCProvider = ({ children }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);

  // IPFS configuration - use direct URLs now that CORS is configured
  const IPFS_API_URL = process.env.REACT_APP_IPFS_API_URL || 'https://serenity.ing/api/v0';
  const IPFS_GATEWAY_URL = process.env.REACT_APP_IPFS_GATEWAY_URL || 'https://serenity.ing/ipfs';

  // Applications index IPFS hash (stored in localStorage for now)
  const APPLICATIONS_INDEX_KEY = 'kyc_applications_index_hash';

  // Fallback flag for localStorage KYC (set in .env as USE_KYC_LOCALSTORAGE_FALLBACK=true)
  const USE_KYC_LOCALSTORAGE_FALLBACK = process.env.REACT_APP_USE_KYC_LOCALSTORAGE_FALLBACK === 'true';

  useEffect(() => {
    loadKYCApplications();
  }, []);

  // Load all KYC applications from IPFS
  const loadKYCApplications = async () => {
    setLoading(true);
    try {
      const indexHash = localStorage.getItem(APPLICATIONS_INDEX_KEY);
      if (indexHash) {
        // If fallback flag is set, or in development or hash is a mock, load from localStorage
        if (
          USE_KYC_LOCALSTORAGE_FALLBACK ||
          process.env.NODE_ENV === 'development' ||
          indexHash.startsWith('QmMock') ||
          indexHash.startsWith('ipfs_mock_')
        ) {
          // Load the applications index from localStorage
          const indexDataRaw = localStorage.getItem(`ipfs_mock_${indexHash}`);
          if (indexDataRaw) {
            const applicationsIndex = JSON.parse(indexDataRaw);
            // For each application, if its ipfsHash is a mock, load from localStorage
            const loadedApplications = (applicationsIndex.applications || []).map(app => {
              if (app.ipfsHash && app.ipfsHash.startsWith('QmMock')) {
                const appDataRaw = localStorage.getItem(`ipfs_mock_${app.ipfsHash}`);
                if (appDataRaw) {
                  try {
                    return JSON.parse(appDataRaw);
                  } catch (e) {
                    return app;
                  }
                }
              }
              return app;
            });
            setApplications(loadedApplications);
            return;
          }
        } else {
          const response = await fetch(`${IPFS_GATEWAY_URL}/${indexHash}`);
          if (response.ok) {
            const applicationsIndex = await response.json();
            setApplications(applicationsIndex.applications || []);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load KYC applications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Upload file to IPFS
  const uploadFileToIPFS = async (file) => {
    // For development or fallback, use mock IPFS immediately while server setup is in progress
    if (USE_KYC_LOCALSTORAGE_FALLBACK || process.env.NODE_ENV === 'development') {
      console.warn('Development mode: Using mock IPFS hash for file upload');
      const mockHash = `QmMockFile${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
      
      // Store the file in localStorage for development
      const reader = new FileReader();
      reader.onload = function(e) {
        localStorage.setItem(`ipfs_mock_${mockHash}`, e.target.result);
      };
      reader.readAsDataURL(file);
      
      console.log(`Mock file uploaded: ${file.name} -> ${mockHash}`);
      return mockHash;
    }
    
    try {
      console.log(`Uploading file to IPFS: ${file.name} (${file.size} bytes)`);
      
      const formData = new FormData();
      formData.append('file', file);

      console.log('IPFS API URL:', IPFS_API_URL);
      
      const response = await fetch(`${IPFS_API_URL}/add`, {
        method: 'POST',
        body: formData
      });

      console.log('IPFS file upload response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('IPFS upload error response:', errorText);
        throw new Error(`Failed to upload file to IPFS: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('File uploaded successfully:', result);
      return result.Hash;

    } catch (error) {
      console.error('Error uploading file to IPFS:', error);
      throw error;
    }
  };

  // Upload JSON data to IPFS
  const uploadJSONToIPFS = async (data) => {
    // For development or fallback, use mock IPFS immediately while server setup is in progress
    if (USE_KYC_LOCALSTORAGE_FALLBACK || process.env.NODE_ENV === 'development') {
      console.warn('Development mode: Using mock IPFS hash for JSON upload');
      const mockHash = `QmMockJSON${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
      
      // Store the JSON in localStorage for development
      localStorage.setItem(`ipfs_mock_${mockHash}`, JSON.stringify(data));
      
      console.log(`Mock JSON uploaded:`, Object.keys(data), `-> ${mockHash}`);
      return mockHash;
    }
    
    try {
      console.log('Uploading JSON to IPFS:', Object.keys(data));
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const formData = new FormData();
      formData.append('file', blob);

      const response = await fetch(`${IPFS_API_URL}/add`, {
        method: 'POST',
        body: formData
      });

      console.log('IPFS JSON upload response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('IPFS JSON upload error response:', errorText);
        throw new Error(`Failed to upload JSON to IPFS: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('JSON uploaded successfully:', result);
      return result.Hash;
    } catch (error) {
      console.error('Error uploading JSON to IPFS:', error);
      throw error;
    }
  };

  // Submit new KYC application
  const submitKYCApplication = async (formData) => {
    try {
      setLoading(true);
      console.log('Submitting KYC application:', formData);

      // Generate application ID if not provided
      const applicationId = formData.applicationId || `KYC_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Upload primary ID document to IPFS
      let primaryDocumentHash = null;
      if (formData.primaryId?.file) {
        console.log('Uploading primary ID document:', formData.primaryId.file.name);
        primaryDocumentHash = await uploadFileToIPFS(formData.primaryId.file);
        console.log('Primary ID document uploaded:', primaryDocumentHash);
      }

      // Upload secondary ID document to IPFS if provided
      let secondaryDocumentHash = null;
      if (formData.secondaryId?.file) {
        console.log('Uploading secondary ID document:', formData.secondaryId.file.name);
        secondaryDocumentHash = await uploadFileToIPFS(formData.secondaryId.file);
        console.log('Secondary ID document uploaded:', secondaryDocumentHash);
      }

      // Prepare application data
      const applicationData = {
        applicationId,
        submissionDate: formData.submissionDate || new Date().toISOString(),
        status: 'pending',
        applicantName: `${formData.firstName} ${formData.middleName || ''} ${formData.lastName}`.trim(),
        walletAddress: formData.walletAddress,
        
        // Personal Information
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        nationality: formData.nationality,
        gender: formData.gender,
        
        // Contact Information
        email: formData.email,
        phone: formData.phone,
        streetAddress: formData.streetAddress,
        suburb: formData.suburb,
        state: formData.state,
        postcode: formData.postcode,
        country: formData.country,
        
        // Identity Documents
        primaryId: {
          type: formData.primaryId?.type,
          number: formData.primaryId?.number,
          expiryDate: formData.primaryId?.expiryDate,
          issuingAuthority: formData.primaryId?.issuingAuthority,
          ipfsHash: primaryDocumentHash
        },
        
        // Secondary ID (optional)
        secondaryId: formData.secondaryId?.type ? {
          type: formData.secondaryId.type,
          number: formData.secondaryId.number,
          expiryDate: formData.secondaryId.expiryDate,
          issuingAuthority: formData.secondaryId.issuingAuthority,
          ipfsHash: secondaryDocumentHash
        } : null,
        
        // Additional Information
        occupation: formData.occupation,
        employer: formData.employer,
        sourceOfFunds: formData.sourceOfFunds,
        purposeOfAccount: formData.purposeOfAccount,
        estimatedActivity: formData.estimatedActivity,
        
        // Declarations
        agreeToTerms: formData.agreeToTerms,
        agreeToPrivacy: formData.agreeToPrivacy,
        confirmInformation: formData.confirmInformation,
        
        // Metadata
        ipfsHash: null, // Will be set after uploading
        lastUpdated: new Date().toISOString()
      };

      console.log('Prepared application data:', applicationData);

      // Upload application data to IPFS
      const applicationHash = await uploadJSONToIPFS(applicationData);
      applicationData.ipfsHash = applicationHash;
      console.log('Application data uploaded to IPFS:', applicationHash);

      // Update applications index
      const newApplications = [...applications, {
        applicationId,
        applicantName: applicationData.applicantName,
        walletAddress: formData.walletAddress,
        submissionDate: applicationData.submissionDate,
        status: 'pending',
        ipfsHash: applicationHash
      }];

      // Upload updated index to IPFS
      const indexData = {
        lastUpdated: new Date().toISOString(),
        totalApplications: newApplications.length,
        applications: newApplications
      };

      const indexHash = await uploadJSONToIPFS(indexData);
      console.log('Applications index updated:', indexHash);
      
      // Store new index hash
      localStorage.setItem(APPLICATIONS_INDEX_KEY, indexHash);
      
      // Update local state
      setApplications(newApplications);

      console.log('KYC Application submitted successfully:', {
        applicationId,
        applicationHash,
        indexHash
      });

      return { applicationId, hash: applicationHash };

    } catch (error) {
      console.error('Failed to submit KYC application:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load full KYC application data
  const loadKYCApplication = async (ipfsHash) => {
    try {
      // If fallback flag is set, or in development or hash is a mock, load from localStorage
      if (
        USE_KYC_LOCALSTORAGE_FALLBACK ||
        process.env.NODE_ENV === 'development' ||
        ipfsHash.startsWith('QmMock') ||
        ipfsHash.startsWith('ipfs_mock_')
      ) {
        const appDataRaw = localStorage.getItem(`ipfs_mock_${ipfsHash}`);
        if (appDataRaw) {
          return JSON.parse(appDataRaw);
        } else {
          // Try to find the summary object from the applications list
          const summary = applications.find(app => app.ipfsHash === ipfsHash);
          if (summary) {
            // Hydrate with all expected fields to prevent UI errors
            return {
              ...summary,
              firstName: summary.firstName || '',
              middleName: summary.middleName || '',
              lastName: summary.lastName || '',
              dateOfBirth: summary.dateOfBirth || '',
              nationality: summary.nationality || '',
              gender: summary.gender || '',
              email: summary.email || '',
              phone: summary.phone || '',
              streetAddress: summary.streetAddress || '',
              suburb: summary.suburb || '',
              state: summary.state || '',
              postcode: summary.postcode || '',
              country: summary.country || '',
              address: {
                street: summary.streetAddress || '',
                suburb: summary.suburb || '',
                state: summary.state || '',
                postcode: summary.postcode || '',
                country: summary.country || ''
              },
              primaryId: {
                type: summary.primaryId?.type || '',
                number: summary.primaryId?.number || '',
                expiryDate: summary.primaryId?.expiryDate || '',
                issuingAuthority: summary.primaryId?.issuingAuthority || '',
                ipfsHash: summary.primaryId?.ipfsHash || ''
              },
              secondaryId: summary.secondaryId ? {
                type: summary.secondaryId?.type || '',
                number: summary.secondaryId?.number || '',
                expiryDate: summary.secondaryId?.expiryDate || '',
                issuingAuthority: summary.secondaryId?.issuingAuthority || '',
                ipfsHash: summary.secondaryId?.ipfsHash || ''
              } : null,
              occupation: summary.occupation || '',
              employer: summary.employer || '',
              sourceOfFunds: summary.sourceOfFunds || '',
              purposeOfAccount: summary.purposeOfAccount || '',
              estimatedActivity: summary.estimatedActivity || '',
              agreeToTerms: summary.agreeToTerms || false,
              agreeToPrivacy: summary.agreeToPrivacy || false,
              confirmInformation: summary.confirmInformation || false,
              lastUpdated: summary.lastUpdated || '',
              statusNotes: summary.statusNotes || '',
              statusUpdatedDate: summary.statusUpdatedDate || ''
            };
          }
          throw new Error('No local application data found');
        }
      }
      // Otherwise, fetch from IPFS
      const response = await fetch(`${IPFS_GATEWAY_URL}/${ipfsHash}`);
      if (!response.ok) {
        throw new Error('Failed to load application from IPFS');
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to load KYC application:', error);
      throw error;
    }
  };

  // Load application document (ID image/PDF)
  const loadApplicationDocument = (ipfsHash) => {
    return `${IPFS_GATEWAY_URL}/${ipfsHash}`;
  };

  // Update application status
  const updateApplicationStatus = async (applicationId, status, notes = '') => {
    try {
      setLoading(true);

      // Find the application
      const application = applications.find(app => app.applicationId === applicationId);
      if (!application) {
        throw new Error('Application not found');
      }

      // Load full application data
      const fullData = await loadKYCApplication(application.ipfsHash);
      
      // Update status and notes
      const updatedData = {
        ...fullData,
        status,
        statusNotes: notes,
        statusUpdatedDate: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };

      // Upload updated application data
      const newApplicationHash = await uploadJSONToIPFS(updatedData);

      // Update applications index
      const updatedApplications = applications.map(app =>
        app.applicationId === applicationId
          ? { ...app, status, ipfsHash: newApplicationHash }
          : app
      );

      // Upload updated index
      const indexData = {
        lastUpdated: new Date().toISOString(),
        totalApplications: updatedApplications.length,
        applications: updatedApplications
      };

      const indexHash = await uploadJSONToIPFS(indexData);
      localStorage.setItem(APPLICATIONS_INDEX_KEY, indexHash);
      
      // Update local state
      setApplications(updatedApplications);

      console.log('Application status updated:', { applicationId, status, newHash: newApplicationHash });

    } catch (error) {
      console.error('Failed to update application status:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get applications by status
  const getApplicationsByStatus = (status) => {
    if (status === 'all') return applications;
    return applications.filter(app => app.status === status);
  };

  // Get application statistics
  const getApplicationStats = () => {
    const stats = {
      total: applications.length,
      pending: 0,
      underReview: 0,
      approved: 0,
      rejected: 0
    };

    applications.forEach(app => {
      switch (app.status) {
        case 'pending':
          stats.pending++;
          break;
        case 'under_review':
          stats.underReview++;
          break;
        case 'approved':
          stats.approved++;
          break;
        case 'rejected':
          stats.rejected++;
          break;
        default:
          break;
      }
    });

    return stats;
  };

  // Test IPFS connection
  const testIPFSConnection = async () => {
    try {
      const testData = { test: true, timestamp: Date.now() };
      const hash = await uploadJSONToIPFS(testData);
      
      // Try to retrieve the data
      const response = await fetch(`${IPFS_GATEWAY_URL}/${hash}`);
      const retrievedData = await response.json();
      
      return {
        success: true,
        hash,
        uploadTime: Date.now() - testData.timestamp,
        data: retrievedData
      };
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  };

  const value = {
    applications,
    loading,
    submitKYCApplication,
    loadKYCApplication,
    loadApplicationDocument,
    updateApplicationStatus,
    getApplicationsByStatus,
    getApplicationStats,
    testIPFSConnection,
    loadKYCApplications
  };

  return (
    <KYCContext.Provider value={value}>
      {children}
    </KYCContext.Provider>
  );
};

export default KYCProvider;
